/**
 * ExpertBriefPage
 * ===============
 * Shown to the expert when they open a piece of work — either a live-session
 * booking (booked via Claude MCP or directly) or an async review submitted
 * via /get-verified. Renders the AI context package + the submitted artifact
 * + meta + a CTA into the verdict-submission flow.
 *
 * Routes (BR-011 Tranche 1 — page is now polymorphic):
 *   /expert/brief/:bookingId          (legacy — live session)
 *   /expert/brief/review/:reviewId    (new — async review)
 *
 * The component reads useParams and figures out the mode:
 *   - reviewId  set → "review" mode  → fetch /api/verifications/{id}/brief
 *   - bookingId set → "booking" mode → fetch /api/bookings/{id}/brief
 *
 * Auth: Expert only (ProtectedRoute).
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  Target,
  MessageSquare,
  HelpCircle,
  AlertTriangle,
  FileText,
  Calendar,
  Clock,
  CheckCircle2,
  ChevronRight,
  ArrowLeft,
  Sparkles,
  Loader2,
  BookOpen,
  ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import MarketingNav from "@/components/MarketingNav";

// ---------------------------------------------------------------------------
// Section card
// ---------------------------------------------------------------------------

function BriefSection({ icon: Icon, title, accentColor = "text-brand-green", children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-canvas-card border border-slateline-200 rounded-sm p-6"
    >
      <div className={`flex items-center gap-2 mb-4 ${accentColor}`}>
        <Icon className="w-5 h-5" />
        <h3 className="font-semibold text-sm uppercase tracking-widest">{title}</h3>
      </div>
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Bullet list
// ---------------------------------------------------------------------------

function BulletList({ items, emptyText = "None provided.", accent = "bg-brand-green" }) {
  if (!items || items.length === 0) {
    return <p className="text-slateline-400 italic text-sm">{emptyText}</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className={`mt-2 w-1.5 h-1.5 rounded-full ${accent} flex-shrink-0`} />
          <span className="text-slateline-600 text-sm leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ExpertBriefPage() {
  // BR-011 — polymorphic: routes are either /expert/brief/:bookingId
  // (legacy live-session) or /expert/brief/review/:reviewId (async review).
  // We read both params; whichever is set drives mode + fetch endpoint.
  const { bookingId, reviewId } = useParams();
  const mode = reviewId ? "review" : "booking";
  const subjectId = reviewId || bookingId;

  const navigate = useNavigate();
  const { user } = useAuth();

  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    fetchBrief();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, mode]);

  const fetchBrief = async () => {
    try {
      setLoading(true);
      const url = mode === "review"
        ? `${API}/verifications/${subjectId}/brief`
        : `${API}/bookings/${subjectId}/brief`;
      const res = await axios.get(url);
      setBrief(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail
        || `Failed to load ${mode === "review" ? "review" : "session"} brief.`;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // BR-011 — Download all attached artifacts as a single zip. Streams from
  // the backend endpoint we built today; the browser saves it via the
  // Content-Disposition header. No third-party libs needed.
  const handleDownloadAllArtifacts = async () => {
    if (mode !== "review" || !subjectId) return;
    setDownloadingZip(true);
    try {
      const res = await axios.get(
        `${API}/verifications/${subjectId}/artifacts.zip`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      // Pull filename from Content-Disposition if present, else derive
      const cd = res.headers["content-disposition"] || "";
      const fnMatch = cd.match(/filename="?([^"]+)"?/);
      const filename = fnMatch ? fnMatch[1] : `xpert-review-${subjectId.slice(0, 8)}.zip`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Files downloaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't download files.";
      toast.error(typeof detail === "string" ? detail : "Couldn't download files.");
    } finally {
      setDownloadingZip(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="max-w-2xl mx-auto px-4 pt-32 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-ink mb-2">Unable to load brief</h2>
          <p className="text-slateline-500 mb-6">{error}</p>
          <Button onClick={() => navigate("/expert-dashboard")} variant="outline">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const pkg = brief?.context_package;
  const hasContext = brief?.has_ai_context && pkg;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />

      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16">

        {/* Back nav */}
        <button
          onClick={() => navigate("/expert-dashboard")}
          className="flex items-center gap-2 text-slateline-500 hover:text-ink transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            {hasContext ? (
              <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-brand-green" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-slateline-200/50 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-slateline-500" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-ink">
                {mode === "review"
                  ? "Async Review Brief"
                  : hasContext
                  ? "AI Session Brief"
                  : "Session Details"}
              </h1>
              <p className="text-slateline-500 text-sm">
                {mode === "review"
                  ? "Async review submitted via /get-verified — read the brief below, then submit your verdict."
                  : hasContext
                  ? "This session was booked via Claude — context package below"
                  : "Session booked via Xpert directly"}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Meta block — different fields for booking vs review. For a
            review, we show the deadline + turnaround + category; for a
            booking we show the original date/time/duration. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8"
        >
          {mode === "review" ? (
            <>
              <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4">
                <div className="flex items-center gap-2 text-slateline-500 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs">Due by</span>
                </div>
                <p className="font-medium text-ink text-sm">
                  {brief.due_at
                    ? new Date(brief.due_at).toLocaleString("en-US", {
                        weekday: "short", month: "short", day: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : "—"}
                </p>
              </div>
              <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4">
                <div className="flex items-center gap-2 text-slateline-500 mb-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs">Turnaround</span>
                </div>
                <p className="font-medium text-ink text-sm">
                  {brief.turnaround_hours ? `${brief.turnaround_hours} hours` : "—"}
                </p>
              </div>
              <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4">
                <div className="flex items-center gap-2 text-slateline-500 mb-1">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-xs">Category</span>
                </div>
                <p className="font-medium text-ink text-sm">
                  {brief.category_label || brief.category || "—"}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4">
                <div className="flex items-center gap-2 text-slateline-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs">Date</span>
                </div>
                <p className="font-medium text-ink text-sm">
                  {brief.scheduled_date
                    ? new Date(brief.scheduled_date).toLocaleDateString("en-US", {
                        weekday: "long", month: "long", day: "numeric",
                      })
                    : "—"}
                </p>
              </div>
              <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4">
                <div className="flex items-center gap-2 text-slateline-500 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs">Time (UTC)</span>
                </div>
                <p className="font-medium text-ink text-sm">{brief.scheduled_time || "—"}</p>
              </div>
              <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4">
                <div className="flex items-center gap-2 text-slateline-500 mb-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs">Duration</span>
                </div>
                <p className="font-medium text-ink text-sm">
                  {brief.duration_minutes ? `${brief.duration_minutes} min` : "—"}
                </p>
              </div>
            </>
          )}
        </motion.div>

        {/* Submission text (review mode only) — what the buyer pasted into
            the /get-verified form. This is the AI output they want reviewed. */}
        {mode === "review" && brief.submission_text && (
          <BriefSection icon={FileText} title="Submitted AI Output">
            <div className="bg-canvas border border-slateline-200 rounded-sm p-4 max-h-96 overflow-auto">
              <pre className="text-slateline-700 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                {brief.submission_text}
              </pre>
            </div>
          </BriefSection>
        )}

        {/* Download-all bar (review mode + at least one file artifact) */}
        {mode === "review" && pkg?.artifacts?.some?.((a) => a?.kind === "file") && (
          <div className="mb-6 mt-6 flex items-center justify-between p-4 bg-brand-greenPale border border-brand-green/30 rounded-sm">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-brand-green" />
              <div>
                <p className="text-sm font-medium text-ink">Attached files</p>
                <p className="text-xs text-slateline-500">
                  {pkg.artifacts.filter((a) => a?.kind === "file").length} file(s) — download as zip
                </p>
              </div>
            </div>
            <Button
              onClick={handleDownloadAllArtifacts}
              disabled={downloadingZip}
              className="bg-brand-green hover:bg-brand-greenHover text-white"
            >
              {downloadingZip ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              {downloadingZip ? "Packaging…" : "Download all"}
            </Button>
          </div>
        )}

        {/* Client notes (from booking) */}
        {brief.client_notes && (
          <div className="mb-6 p-4 bg-canvas-card border border-slateline-200 rounded-sm">
            <p className="text-xs text-slateline-400 flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4" />
              Client Note (from booking)
            </p>
            <p className="text-slateline-600 text-sm">{brief.client_notes}</p>
          </div>
        )}

        {/* No context package */}
        {!hasContext && (
          <div className="bg-canvas-card border border-slateline-200 rounded-sm p-8 text-center">
            <BookOpen className="w-10 h-10 text-slateline-300 mx-auto mb-3" />
            <p className="text-slateline-500">
              No AI context package for this session. The client booked directly.
            </p>
          </div>
        )}

        {/* AI Context Package */}
        {hasContext && (
          <div className="space-y-4">

            {/* AI Generated banner */}
            <div className="flex items-center gap-3 px-4 py-3 bg-brand-green/5 border border-brand-green/20 rounded-sm">
              <Brain className="w-5 h-5 text-brand-green flex-shrink-0" />
              <p className="text-sm text-brand-green">
                The following context was generated by Claude from the client's conversation.
                Use it to prepare — the client will not need to repeat their full backstory.
              </p>
            </div>

            {/* User Goal */}
            <BriefSection icon={Target} title="Client's Goal">
              <p className="text-slateline-600 leading-relaxed">
                {pkg.user_goal || <span className="text-slateline-400 italic">Not specified.</span>}
              </p>
            </BriefSection>

            {/* Conversation Summary */}
            <BriefSection icon={MessageSquare} title="Conversation Summary">
              <p className="text-slateline-600 text-sm leading-relaxed whitespace-pre-wrap">
                {pkg.conversation_summary || <span className="text-slateline-400 italic">Not provided.</span>}
              </p>
            </BriefSection>

            {/* What AI covered */}
            {pkg.what_ai_covered && (
              <BriefSection icon={Brain} title="What Claude Already Covered" accentColor="text-blue-400">
                <p className="text-slateline-600 text-sm leading-relaxed whitespace-pre-wrap">
                  {pkg.what_ai_covered}
                </p>
              </BriefSection>
            )}

            {/* Uncertainty Areas */}
            <BriefSection icon={AlertTriangle} title="Areas of Uncertainty" accentColor="text-amber-400">
              <BulletList
                items={pkg.uncertainty_areas}
                emptyText="No uncertainty areas flagged."
                accent="bg-amber-500"
              />
            </BriefSection>

            {/* Key Questions */}
            <BriefSection icon={HelpCircle} title="Key Questions for You" accentColor="text-brand-green">
              <BulletList
                items={pkg.key_questions}
                emptyText="No specific questions provided."
                accent="bg-brand-green"
              />
            </BriefSection>

            {/* Artifacts */}
            {pkg.artifacts && pkg.artifacts.length > 0 && (
              <BriefSection icon={FileText} title="Shared Artifacts">
                <ul className="space-y-2">
                  {pkg.artifacts.map((art, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 bg-canvas rounded-sm border border-slateline-200">
                      <FileText className="w-4 h-4 text-slateline-500 flex-shrink-0" />
                      <span className="text-slateline-600 text-sm">{typeof art === "string" ? art : art.name || JSON.stringify(art)}</span>
                    </li>
                  ))}
                </ul>
              </BriefSection>
            )}

            {/* Generated at */}
            {pkg.context_generated_at && (
              <p className="text-xs text-slateline-300 text-right">
                Context generated {new Date(pkg.context_generated_at).toLocaleString()}
                {pkg.ai_model ? ` · ${pkg.ai_model}` : ""}
              </p>
            )}
          </div>
        )}

        {/* CTA row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          {!brief.verdict_submitted ? (
            <Button
              onClick={() =>
                navigate(
                  mode === "review"
                    ? `/expert/verdict/review/${subjectId}`
                    : `/expert/verdict/${subjectId}`
                )
              }
              className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold"
            >
              <ListChecks className="w-4 h-4 mr-2" />
              {mode === "review" ? "Submit Verdict" : "Submit Verdict After Session"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-brand-green/10 border border-brand-green/30 rounded-sm">
              <CheckCircle2 className="w-4 h-4 text-brand-green" />
              <span className="text-brand-green text-sm font-medium">Verdict submitted</span>
              <button
                onClick={() =>
                  navigate(
                    mode === "review"
                      ? `/expert/verdict/review/${subjectId}`
                      : `/expert/verdict/${subjectId}`
                  )
                }
                className="text-brand-green text-sm underline ml-2"
              >
                Edit
              </button>
            </div>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/expert-dashboard")}
            className="border-slateline-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </motion.div>

      </div>
    </div>
  );
}
